import {
  AudienceIcon,
  Box,
  Column,
  Row,
  SyncIcon,
  Text,
  TextWithTooltip,
} from "@hightouch/htui";

import type {
  AmpAttachableAudiencesQuery,
  AmpAttachableSyncsQuery,
} from "src/graphql";
import { LastUpdatedCell } from "src/ui/table/columns/last-updated";
import { Table, type TableColumn } from "src/ui/table/table";
import { formatDate } from "src/utils/time";

export type AudienceOption = AmpAttachableAudiencesQuery["segments"][number];
export type SyncOption =
  AmpAttachableSyncsQuery["destination_instances"][number];

type AttachableTableProps<T extends { id: string | number }> = {
  rows: T[];
  /** The checkbox name for a row. */
  rowLabel: (row: T) => string;
  selectedIds: string[];
  onToggle: (row: T) => void;
  /** The header checkbox: the shown rows that end up selected, which is none or all of them. */
  onSelectShown: (selected: T[]) => void;
  isDisabled?: boolean;
  isLoading?: boolean;
};

const DASH = "--";

function NameCell({
  name,
  description,
  icon,
}: {
  name: string;
  description?: string | null;
  icon: typeof AudienceIcon;
}) {
  return (
    <Row align="center" gap={3} minWidth={0}>
      <Box
        as="span"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        boxSize={10}
        borderRadius="lg"
        bg="base.lightBackground"
        borderWidth="hairline"
        borderColor="base.border"
        color="text.secondary"
      >
        <Box as={icon} boxSize={5} />
      </Box>
      <Column minWidth={0}>
        <TextWithTooltip fontWeight="medium">{name}</TextWithTooltip>
        {description && (
          <TextWithTooltip size="sm" color="text.secondary">
            {description}
          </TextWithTooltip>
        )}
      </Column>
    </Row>
  );
}

function lastRunLabel(lastRunAt: string | null | undefined) {
  return lastRunAt ? `Last run ${formatDate(lastRunAt)}` : DASH;
}

/** The latest run across a set of syncs, as an ISO timestamp. */
function latestRun(runs: Array<string | null | undefined>) {
  return runs.reduce<string | null>(
    (latest, run) => (run && (!latest || run > latest) ? run : latest),
    null,
  );
}

const audienceColumns: TableColumn<AudienceOption>[] = [
  {
    name: "Name",
    min: "240px",
    max: "2fr",
    cell: (audience) => (
      <NameCell
        name={audience.name ?? "Untitled audience"}
        description={audience.description}
        icon={AudienceIcon}
      />
    ),
  },
  {
    name: "Size",
    max: "max-content",
    cell: (audience) => (
      <Text color="text.secondary">
        {audience.last_run_size == null
          ? DASH
          : formatCount(audience.last_run_size)}
      </Text>
    ),
  },
  {
    name: "Syncing to",
    max: "max-content",
    cell: (audience) => {
      const destinations = [
        ...new Set(
          audience.destination_instances.flatMap((sync) =>
            sync.destination ? [sync.destination.name] : [],
          ),
        ),
      ];
      return (
        <TextWithTooltip color="text.secondary">
          {destinations.length > 0 ? destinations.join(", ") : "No syncs"}
        </TextWithTooltip>
      );
    },
  },
  {
    name: "Last sync run",
    max: "max-content",
    cell: (audience) => (
      <Text color="text.secondary">
        {lastRunLabel(
          latestRun(audience.destination_instances.map((s) => s.last_run_at)),
        )}
      </Text>
    ),
  },
  {
    name: "Last updated",
    max: "max-content",
    cell: (audience) => (
      <LastUpdatedCell
        timestamp={audience.updated_at}
        name={audience.updated_by_user?.name}
      />
    ),
  },
];

const syncColumns: TableColumn<SyncOption>[] = [
  {
    name: "Name",
    min: "240px",
    max: "2fr",
    cell: (sync) => (
      <NameCell
        name={sync.segment?.name ?? `Sync ${sync.id}`}
        description={sync.destination ? `To ${sync.destination.name}` : null}
        icon={SyncIcon}
      />
    ),
  },
  {
    name: "Size",
    max: "max-content",
    cell: (sync) => (
      <Text color="text.secondary">
        {sync.segment?.last_run_size == null
          ? DASH
          : formatCount(sync.segment.last_run_size)}
      </Text>
    ),
  },
  {
    name: "Syncing to",
    max: "max-content",
    cell: (sync) => (
      <TextWithTooltip color="text.secondary">
        {sync.destination?.name ?? DASH}
      </TextWithTooltip>
    ),
  },
  {
    name: "Last sync run",
    max: "max-content",
    cell: (sync) => (
      <Text color="text.secondary">{lastRunLabel(sync.last_run_at)}</Text>
    ),
  },
  {
    name: "Last updated",
    max: "max-content",
    cell: (sync) => (
      <LastUpdatedCell
        timestamp={sync.updated_at}
        name={sync.updated_by_user?.name}
      />
    ),
  },
];

function formatCount(count: number) {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  })
    .format(count)
    .toLowerCase();
}

function AttachableTable<T extends { id: string | number }>({
  columns,
  rows,
  rowLabel,
  selectedIds,
  onToggle,
  onSelectShown,
  isDisabled,
  isLoading,
}: AttachableTableProps<T> & { columns: TableColumn<T>[] }) {
  return (
    <Box
      borderWidth="hairline"
      borderColor="base.border"
      borderRadius="xl"
      overflow="hidden"
    >
      <Table
        columns={columns}
        data={rows}
        loading={isLoading}
        primaryKey="id"
        selectedRows={selectedIds}
        disabled={() => Boolean(isDisabled)}
        rowHeight="72px"
        selectLabel={rowLabel}
        onSelect={(key: unknown) => {
          if (Array.isArray(key)) {
            const keys = new Set(key.map(String));
            onSelectShown(rows.filter((row) => keys.has(String(row.id))));
            return;
          }
          const row = rows.find((other) => String(other.id) === String(key));
          if (row) onToggle(row);
        }}
      />
    </Box>
  );
}

/** Audiences as a table of checkbox rows. */
export function AudienceTable(props: AttachableTableProps<AudienceOption>) {
  return <AttachableTable columns={audienceColumns} {...props} />;
}

/** Syncs as a table of checkbox rows. */
export function SyncTable(props: AttachableTableProps<SyncOption>) {
  return <AttachableTable columns={syncColumns} {...props} />;
}

const NO_ROWS: never[] = [];

/** The placeholder rows an audience or sync pane shows on its cold load. */
export function AttachableTableSkeleton({
  kind,
}: {
  kind: "audience" | "sync";
}) {
  const props = {
    rows: NO_ROWS,
    rowLabel: () => "",
    selectedIds: [],
    onToggle: () => {},
    onSelectShown: () => {},
    isLoading: true,
  };
  return kind === "audience" ? (
    <AudienceTable {...props} />
  ) : (
    <SyncTable {...props} />
  );
}
